import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertService } from '@core/services/alert.service';
import { CommonService } from '@core/services/common.service';
import { CorrespondenceService } from '@core/services/correspondence.service';
import { DataService } from '@core/services/data.service';
import { PolicyService } from '@core/services/policy.service';
import { TranslateService } from '@ngx-translate/core';
import { Agent } from '@shared/models/agent';
import { Codebook } from '@shared/models/codebooks/codebook';
import { PolicyStates } from '@shared/models/codebooks/policy-state';
import { Correspondence } from '@shared/models/correspondence';
import { CorrespondenceGroup } from '@shared/models/correspondence-group';
import { CpcPayment } from '@shared/models/cpc-payment';
import { GroupedCorrespondence } from '@shared/models/grouped-correspondence';
import { MaInsurance } from '@shared/models/ma-insurance';
import { PaymentStatus, PaymentStatusVariant } from '@shared/models/payment-status';
import { Policy } from '@shared/models/policy';
import { Premium } from '@shared/models/premium';
import { User } from '@shared/models/user';
import { interval, Subscription } from 'rxjs';
import { PoliciesComponent } from '../policies.component';
import { LoadingService } from '@core/services/loading.service';


@Component({
    selector: 'app-policy-detail',
    templateUrl: './policy-detail.component.html',
    styleUrls: ['./policy-detail.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PolicyDetailComponent extends PoliciesComponent implements OnInit {

    public loggedUser: User;

    readonly policyNumber: string;
    readonly policyState: string;

    readonly paymentStatusVariant: typeof PaymentStatusVariant = PaymentStatusVariant;
    readonly paymentStatus: typeof PaymentStatus = PaymentStatus;
    readonly correspondenceGroup: typeof CorrespondenceGroup = CorrespondenceGroup;
    readonly policyStates: PolicyStates;
    readonly paymentSources: Codebook[];
    readonly paymentModes: Codebook[];

    public policy: Policy;
    public upcomingPremiumList: Premium[];
    public upcomingPaymentList: CpcPayment[];
    public pastPremiumList: Premium[];
    public pastPaymentList: CpcPayment[];

    public groupedCorrespondence: GroupedCorrespondence[] = [];
    public selectedCorrespondence: Correspondence[] = [];
    public downLoadDocumentUrl: string;
    public agents: Agent[] = [];
    public paymentsTabIndex: number;
    // create class for it
    public premiumColLabels = {
        status: '',
        due: '',
        date: '',
        method: '',
        amount: ''
    };

    //private firstload = true;

    public drawerOpenModel = false;
    public clickedItem = false;
    public maInsurance: MaInsurance;

    public disableButtonDownloadAllArr = ['preparing', 'in-progress', 'fail', 'nothing'];
    public clickedDownloadSuccess = false;
    public downloadAllEstimatedTime: number;

    protected checkInterval: Subscription;
    protected _interval = interval(1000);

    protected downloadDisabled = false;

    public agentSource: number;

    constructor(
        public commonService: CommonService,
        protected dataService: DataService,
        protected policyService: PolicyService,

        protected route: ActivatedRoute,
        protected correspondenceService: CorrespondenceService,
        protected translateService: TranslateService,
        protected alertService: AlertService,
        protected loadingService: LoadingService
    ) {
        super(commonService, dataService, policyService);
        this.loggedUser = this.getLoggedUser();

        this.policyStates = this.dataService.policyStates;
        this.paymentSources = this.dataService.codebooks.paymentSources;
        this.paymentModes = this.dataService.codebooks.paymentModes;
        this.policyNumber = this.route.snapshot.paramMap.get('policyNumber');
        this.policyState = this.route.snapshot.paramMap.get('status');
    }

    ngOnInit(): void {
        this.agentSource = this.loggedUser.agentSource;

        // input to paged list
        this.upcomingPaymentList = [];
        this.pastPaymentList = [];
    }

    gtm() {
        this.commonService.gtm();
    }

    downloadAll(): void {

        if (this.downloadDisabled) {
            return;
        }

        if (this.policy.downloadAllStatus != null && this.policy.downloadAllStatus.status == 'success') {
            this.clickedDownloadSuccess = true;
            if (this.checkInterval != null) {
                this.checkInterval.unsubscribe();
            }
            window.open(this.commonService.downloadZipUrl + this.policy.policyNumber, '_blank');
        } else {
            this.CheckStatus();
            this.correspondenceService.postDownloadAll(this.policy.policyNumber).subscribe(downloadAllStatus => {
                this.policy.downloadAllStatus = downloadAllStatus;

                switch (downloadAllStatus.status) {
                    case 'fail':
                        var text = this.translateService.instant('policy.download-all.alertText.' + downloadAllStatus.status);
                        this.alertService.error(text, {
                            autoClose: true
                        });
                        if (this.checkInterval != null) {
                            this.checkInterval.unsubscribe();
                        }
                        break;
                    case 'success':
                        if (this.checkInterval != null) {
                            this.checkInterval.unsubscribe();
                        }
                        break;
                    case 'in-progress':
                        if (this.checkInterval == null) {
                            var text = this.translateService.instant('policy.download-all.alertText.' + downloadAllStatus.status);
                            this.alertService.info(text.replace('TTT', this.downloadAllEstimatedTime), {
                                autoClose: true
                            });
                            this.CheckStatus();
                        }
                        break;
                    case 'preparing':
                        if (this.checkInterval == null) {
                            this.CheckStatus();
                        }
                        break;
                }
            });
        }
    }

    loadDocuments(): void {
        this.policyService.getDocuments(this.policy.policyNumber).subscribe(documents => {
            // documents = all where visible == true
            if (documents.length > 0) {
                this.downloadAllEstimatedTime = documents.length * 3;
            } else {
                this.downloadDisabled = true;
                this.policy.downloadAllStatus.status = 'nothing';
            }
            this.fillDocuments(documents);
        });
    }

    fillDocuments(correspondenceList: Correspondence[]): void {
        if (!correspondenceList) { return; }

        // !! header of policy (top of page) - drawer
        // in header show documents with classid = 7 and subclassIds in (1,2)
        const subclassIds = [1, 2];
        const orderedList = correspondenceList.sort((a, b) => new Date(b.createTs).getTime() - new Date(a.createTs).getTime());
        subclassIds.forEach(subclassid =>
            this.selectedCorrespondence.push(orderedList.find(x => x.classid === 7 && x.subclassid === subclassid)));
        // remove undefined items
        this.selectedCorrespondence = this.selectedCorrespondence.filter(x => x);

        // !! Bottom section
        const correspondenceGroupList = correspondenceList?.map(x => x.classid)
            .filter((value, index, self) => self.indexOf(value) === index);

        this.groupedCorrespondence = [];
        correspondenceGroupList.forEach(group => {
            this.groupedCorrespondence.push(
                new GroupedCorrespondence(
                    group,
                    Object.values(
                        this.dataService.groupByKey(
                            correspondenceList.filter(x => x.classid === Number(group)), // .sort((a, b) => ),
                            'subclassidLabel')
                    )
                )
            );
        });
    }

    openLink(url: string): void {

        if (url == 'edit-payment') {
            this.commonService.openEditPopUpModal('payment');
            return;
        }

        window.open(url, '_blank');
    }

    protected CheckStatus() {
        if (this.checkInterval == null) {
            this._interval = interval(10000);
            this.checkInterval = this._interval.subscribe(x => {
                this.downloadAll();
            });
        }
    }
}
